/**
 * Event Card Component
 * @file src/web/features/events/components/EventCard.tsx
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Image, ArrowRight } from 'lucide-react';
import type { Event } from '../hooks/useEvents';

interface EventCardProps {
  event: Event;
  isAdmin?: boolean;
  onEdit?: (id: number) => void;
  onDelete?: (id: number, title: string) => void;
}

export const EventCard: React.FC<EventCardProps> = ({
  event,
  isAdmin = false,
  onEdit,
  onDelete
}) => {
  const itemCount = event._count?.items || 0;
  const formattedDate = event.eventDate
    ? new Date(event.eventDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      {/* Cover Image */}
      <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-cyan-500 to-blue-600">
        {event.coverImageUrl ? (
          <img
            src={event.coverImageUrl}
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-16 h-16 text-white/50" />
          </div>
        )}

        {/* Item count badge */}
        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5">
          <Image className="w-3.5 h-3.5" />
          <span>{itemCount}</span>
        </div>

        {/* Admin status badge */}
        {isAdmin && (
          <div className="absolute top-3 left-3">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              event.isActive
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}>
              {event.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-1">
          {event.title}
        </h3>

        {event.description && (
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
            {event.description}
          </p>
        )}

        {/* Meta info */}
        <div className="flex flex-wrap gap-3 text-sm text-gray-500 mb-4">
          {formattedDate && (
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{formattedDate}</span>
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span className="line-clamp-1">{event.location}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <Link
            to={`/events/${event.id}`}
            className="inline-flex items-center text-cyan-600 font-semibold hover:text-cyan-700 transition-colors group/link"
          >
            View Photos
            <ArrowRight className="w-4 h-4 ml-1 group-hover/link:translate-x-1 transition-transform" />
          </Link>

          {isAdmin && (
            <div className="flex gap-2">
              {onEdit && (
                <button
                  onClick={() => onEdit(event.id)}
                  className="px-3 py-1.5 text-sm font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                >
                  Edit
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(event.id, event.title)}
                  className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCard;
